import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { COUNTRY_CODES } from '../../constants/countryCodes';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
const PROVIDER_TYPES = [
  'Primary Care Physician',
  'Other Physician',
  'Hospital/ER Provider',
  'Clinic Provider',
  'Group Provider',
  'Referral Provider'
];

const PROVIDER_DESIGNATIONS = {
  'MD': 'Doctor of Medicine: The most common medical degree in the United States; includes Psychiatrists.',
  'DO': 'Doctor of Osteopathic Medicine: A D.O. is a fully licensed physician just like an MD but with training emphasizing a whole-person approach to care.',
  'DMD/DDS': 'Doctor of Medicine in Dentistry or Doctor of Dental Surgery: The education and training for D.D.S. and D.M.D. degrees are identical. Endodontists have an additional 2-3 years of specialized residency focusing on dental pulp and roots.',
  'DPM': 'Doctor of Podiatric Medicine: Podiatrists specialize in the diagnosis, treatment, and prevention of disorders and injuries of the foot, ankle, and related structures of the leg.',
  'OD': 'Doctor of Optometry: Optometrists are eye care specialists who perform eye exams and vision tests, prescribe and dispense corrective lenses, and can prescribe some medications for eye diseases. They are not medical doctors and do not perform surgery.',
  'DC': 'Doctor of Chiropractic: Chiropractors focus on disorders of the musculoskeletal and nervous systems. Primary focus on manual therapy; cannot prescribe medications or perform surgery.',
  'PsyD': 'Doctor of Psychology: Clinical psychologists diagnose and treat mental and behavioral conditions through therapy. They are not medical doctors and generally cannot prescribe medication.',
  'RN': 'Registered Nurse: Licensed healthcare professional who has completed an approved nursing program and passed a national licensing exam. RNs provide and coordinate patient care and collaborate with doctors.',
  'DNP': 'Doctor of Nursing Practice: The DNP is a terminal, practice-focused degree that prepares nurses for the highest level of clinical leadership and practice. Graduates are trained to apply research to improve healthcare outcomes.',
  'BSN/MSN': 'Bachelor of Science or Masters of Science in Nursing: Academic degrees (not a license, but many employers, particularly hospitals, prefer or require for RNs. It prepares nurses for greater responsibility, leadership, and specialization.',
  'LPN/LVN': 'Licensed Practical Nurse or Licensed Vocational Nurse: Typically the entry point for licensed nursing. These nurses complete a certificate or diploma program (usually one year) and provide basic patient care under the supervision of a RN or MD.',
  'CNA': 'Certified Nursing Assistant: CNAs are not licensed nurses but are trained to assist patients with daily activities under the supervision of an LPN or RN.',
  'NP/PA': 'Nurse Practitioner or Physician\'s Assistant: Has advanced training to diagnose and treat illnesses and prescribe medication. Generally work under the supervision of an MD or DO with most holding advanced academic nursing degrees.',
  'CRNA': 'Certified Registered Nurse Anesthetist: CRNAs administer anesthesia and provide related care before, during, and after surgical procedures. A Doctor of Nursing Practice (DNP) is now required for entry into this specialty.',
  'CNM': 'Certified Nurse-Midwife: CNMs provide comprehensive care for women, focusing on pregnancy, childbirth, and gynecologic health.',
  'CNS': 'Clinical Nurse Specialist: CNSs are experts in a specific area of practice (e.g., pediatrics, oncology) and use evidence-based practice to improve patient care and nursing procedures within a healthcare setting.'
};

const PROVIDER_SPECIALTIES = [
  'Internal Medicine', 'Family Medicine', 'General Surgery', 'Dentist',
  'Cardiovascular', 'Endocrinology', 'Oncology', 'Emergency Medicine',
  'Orthopedics', 'Urology', 'Chiropractic', 'OB/GYN', 'Pediatrics',
  'Gastroenterology', 'Infectious Diseases', 'Nephrology', 'Pulmonology',
  'Rheumatology', 'Neurology', 'Cosmetic', 'Otolaryngology', 'Dermatology',
  'Ophthalmology', 'Psychiatry', 'Rehabilitation', 'Physical Therapy',
  'Genetics/Genomics', 'Nuclear Medicine'
];

function HealthProviderForm({ onClose, onSave, fetchWithAuth, editingProvider }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [formData, setFormData] = useState({
    provider_name: '',
    company_association: '',
    provider_type: '',
    provider_designation: '',
    provider_address: '',
    phone_country_code: '+1',
    provider_phone: '',
    provider_email: '',
    provider_specialty: '',
    provider_description: ''
  });

  const [showTooltip, setShowTooltip] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Populate form with editing data
  useEffect(() => {
    if (editingProvider) {
      setFormData({
        provider_name: editingProvider.provider_name || '',
        company_association: editingProvider.company_association || '',
        provider_type: editingProvider.provider_type || '',
        provider_designation: editingProvider.provider_designation || '',
        provider_address: editingProvider.provider_address || '',
        phone_country_code: editingProvider.phone_country_code || '+1',
        provider_phone: editingProvider.provider_phone || '',
        provider_email: editingProvider.provider_email || '',
        provider_specialty: editingProvider.provider_specialty || '',
        provider_description: editingProvider.provider_description || ''
      });
    }
  }, [editingProvider]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    try {
      const url = editingProvider
        ? `${API_BASE}/api/providers/${editingProvider.id}`
        : `${API_BASE}/api/providers`;

      const method = editingProvider ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method: method,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        const errorData = await response.json();
        if (response.status === 409) {
          setErrorMessage('A provider with this name and email already exists.');
        } else {
          setErrorMessage(errorData.detail || 'Failed to save provider');
        }
      }
    } catch (error) {
      console.error('Failed to save provider:', error);
      setErrorMessage('Failed to save provider. Please try again.');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-content-md">
        <div className="modal-header">
          <h3>{editingProvider ? 'Edit Health Provider' : 'Add Health Provider'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {errorMessage && (
          <div className="tpf-errorMessage">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Provider Name - REQUIRED */}
          <div className="form-group">
            <label className="form-label">Provider Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.provider_name}
              onChange={(e) => setFormData(prev => ({...prev, provider_name: e.target.value}))}
              placeholder="e.g., Dr. John Smith"
              required
            />
          </div>

          {/* Company / Group Association */}
          <div className="form-group">
            <label className="form-label">Company / Group Association</label>
            <input
              type="text"
              className="form-input"
              value={formData.company_association}
              onChange={(e) => setFormData(prev => ({...prev, company_association: e.target.value}))}
              placeholder="e.g., Mayo Clinic, Johns Hopkins"
            />
          </div>

          {/* Provider Type - REQUIRED */}
          <div className="form-group">
            <label className="form-label">Provider Type *</label>
            <select
              className="form-input"
              value={formData.provider_type}
              onChange={(e) => setFormData(prev => ({...prev, provider_type: e.target.value}))}
              required
            >
              <option value="">Select Provider Type...</option>
              {PROVIDER_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Provider Designation - REQUIRED with tooltips */}
          <div className="form-group">
            <label className="form-label">Provider Designation *</label>
            <select
              className="form-input relative"
              value={formData.provider_designation}
              onChange={(e) => setFormData(prev => ({...prev, provider_designation: e.target.value}))}
              required
            >
              <option value="">Select Designation... hover over options for definition</option>
              {Object.keys(PROVIDER_DESIGNATIONS).map(designation => (
                <option
                  key={designation}
                  value={designation}
                  title={PROVIDER_DESIGNATIONS[designation]}
                >
                  {designation}
                </option>
              ))}
            </select>
            <div className="tpf-tooltipHint">
              Hover over each option to see its definition
            </div>
          </div>

          {/* Provider Address */}
          <div className="form-group">
            <label className="form-label">Provider Address</label>
            <textarea
              className="form-textarea"
              value={formData.provider_address}
              onChange={(e) => setFormData(prev => ({...prev, provider_address: e.target.value}))}
              placeholder="Street Address&#10;City, State ZIP&#10;Country"
              rows="3"
            />
          </div>

          {/* Provider Phone with Country Code */}
          <div className="form-group">
            <label className="form-label">Provider Phone</label>
            <div className="flex gap-3">
              <select
                className="form-input tpf-phoneCountryCode"
                value={formData.phone_country_code}
                onChange={(e) => setFormData(prev => ({...prev, phone_country_code: e.target.value}))}

              >
                {COUNTRY_CODES.map(({ code, country }) => (
                  <option key={code} value={code}>
                    {code} ({country})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                className="form-input flex-1"
                value={formData.provider_phone}
                onChange={(e) => setFormData(prev => ({...prev, provider_phone: e.target.value}))}
                placeholder="Phone number"
              />
            </div>
          </div>

          {/* Provider Email - REQUIRED */}
          <div className="form-group">
            <label className="form-label">Provider Email *</label>
            <input
              type="email"
              className="form-input"
              value={formData.provider_email}
              onChange={(e) => setFormData(prev => ({...prev, provider_email: e.target.value}))}
              placeholder="provider@example.com"
              required
            />
          </div>

          {/* Provider Specialty - OPTIONAL */}
          <div className="form-group">
            <label className="form-label">Provider Specialty</label>
            <select
              className="form-input"
              value={formData.provider_specialty}
              onChange={(e) => setFormData(prev => ({...prev, provider_specialty: e.target.value}))}
            >
              <option value="">Select Specialty (Optional)...</option>
              {PROVIDER_SPECIALTIES.map(specialty => (
                <option key={specialty} value={specialty}>{specialty}</option>
              ))}
            </select>
          </div>

          {/* Provider Description */}
          <div className="form-group">
            <label className="form-label">Provider Description</label>
            <textarea
              className="form-textarea"
              value={formData.provider_description}
              onChange={(e) => setFormData(prev => ({...prev, provider_description: e.target.value}))}
              placeholder="Additional notes about this provider..."
              rows="4"
            />
          </div>

          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingProvider ? 'Update Provider' : 'Save Provider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default HealthProviderForm;
